/**
 * HolidayConfigModel Component
 *
 * Responsibilities:
 * - Create new holiday entry
 * - Edit an existing holiday entry
 * - Form handling (name + date)
 * - Input validation + feedback via Snackbar
 * - Firebase DB operations through Holiday Services
 * - Theme-aware modal UI using react-modal + MUI
 * - Loader handling for async operations
 */
import { FunctionComponent, useState, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { createHoliday, editHoliday } from "../../../Services/organizations";
import { showSnackbarAction } from "../../../Redux/actions";
import { themeColors } from "../../theme";
import { v4 as uuidv4 } from 'uuid';
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import Modal, { Styles } from "react-modal";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import { Circles } from "react-loader-spinner";
import StyledCancelButton from "../../Buttons/StyledCancelButton";
import InputTextValue from "../../TextInput/InputTextValue";
import InputTextDate from "../../TextInput/InputTextDate";

/* ------------------------------------------------------------------
   Component Props
   ------------------------------------------------------------------ */
type Props = {
  open: boolean;
  close: () => void;
  organizationId?: any;
  theme?: any;
  edit?: boolean;
  editData?: any;
  editClose?: () => void;
  holidayData?: any;
};

const HolidayConfigModel: FunctionComponent<Props> = ({
  open,
  close,
  organizationId,
  theme,
  edit,
  editData,
  editClose,
  holidayData
}) => {
  /* --------------------------------------------------------------
 STATES FOR SELECTED FIELDS
 -------------------------------------------------------------- */
  const dispatcher = useDispatch();
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
  /** Local loading state */
  const [load, setLoad] = useState(false);

  /** Form values: holiday name + date */
  const [formValues, setFormValues] = useState({ holiday_name: "", date: "" });

  /** -------------------------------------------
     * Populate form if editing an existing holiday
     * ------------------------------------------- */
  useEffect(() => {
    if (edit && editData) {
      setFormValues({
        holiday_name: editData.holiday_name || "",
        date: editData.date || "",
      });
    }
  }, [edit, editData]);

  /** Update local form state */
  const handleChange = (field: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  /** -------------------------------------------
     * Create New Holiday
     * ------------------------------------------- */
  const handleSave = () => {
    const { holiday_name, date } = formValues;
    if (!holiday_name || !date) {
      dispatcher(showSnackbarAction("Please fill all fields", "error"));
      return;
    }
    setLoad(true);
    const HolidayId = uuidv4();
    try {
      createHoliday(
        organizationId,
        holiday_name,
        date,
        HolidayId,
        dispatcher,
        (val) => setLoad(val),
        close
      );
    } catch (error) {
      console.error("Date formatting error:", error);
      dispatcher(showSnackbarAction("Invalid date format", "error"));
      setLoad(false);
    }
  }

  /** -------------------------------------------
   * Edit Existing Holiday
   * ------------------------------------------- */
  const handleEdit = () => {
    setLoad(true);
    editHoliday(
      organizationId,
      formValues.holiday_name,
      formValues.date,
      editData?.holiday_id,
      (val) => setLoad(val),
      close,
      editClose,
      dispatcher
    );
  };

  /** Reset form values */
  const handleClear = () => {
    setFormValues({
      holiday_name: "",
      date: "",
    });
    close();
  };

  /** -------------------------------------------
 * Modal styling with theme-based colors
 * ------------------------------------------- */
  const modalStyle: Styles = {
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)", // darkens the background
      zIndex: 1300, // higher than MUI AppBar/Table
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      background: theme
        ? themeColors.cardBackgroundColor
        : themeColors.cardBackgroundColorDark,
      color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
      borderRadius: 16,
      padding: isMobile ? "24px 12px" : "40px 32px",
      maxWidth: 380,
      minWidth: 320,
      border: "none",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      position: "relative",
      inset: "auto",
      margin: "auto",
    },
  };

  /** ------------------------------------------------------------------
     * Render Component
     * ------------------------------------------------------------------ */
  return (
    <Modal
      isOpen={open}
      onRequestClose={close}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      style={modalStyle}
      ariaHideApp={false}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 340,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <form style={{ width: "100%" }}>
          <Box sx={{ mb: 2 }}>
            {/* ---------------------------
            Header / Title
        ---------------------------- */}
            <h3
              style={{
                color: theme ? "#272944" : "#fff",
                fontFamily: "var(--font-family-main)",
                fontSize: "var(--font-size-large)",
                marginBottom: 24,
                textAlign: "center",
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              {edit === true ? <>Edit Holiday</> : <>Create Holiday</>}
            </h3>
          </Box>

          {/* ---------------------------
            Form Fields
        ---------------------------- */}
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid item xs={12}>
              <InputTextValue
                title="Holiday Name"
                value={formValues.holiday_name}
                onChange={(e) => handleChange("holiday_name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <InputTextDate
                type="date"
                title="Date"
                value={formValues.date}
                onChange={(e: any) => handleChange("date", e.target.value)}
              />
            </Grid>
          </Grid>

          {/* ---------------------------
            Actions: Cancel + Submit
        ---------------------------- */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 4,
                width: "100%",
              }}
            >
              <Box style={{ marginRight: 8 }}>
                <StyledCancelButton onClick={handleClear} isMobile={isMobile}>
                  Cancel
                </StyledCancelButton>
              </Box>
              <Box>
                <StyledSubmitButton
                  onClick={edit ? handleEdit : handleSave}
                  themeMode={themeMUI.palette.mode}
                  isMobile={isMobile}
                >
                  {load ? (
                    <span style={{ display: "flex", justifyContent: "center", height: 24 }}>
                      <Circles color="#fff" height={20} width={20} />
                    </span>
                  ) : (
                    <>Submit</>
                  )}
                </StyledSubmitButton>
              </Box>
            </Box>
          </Grid>
        </form>
      </Box>
    </Modal>
  );
};

/** Map Redux store data to props */
const mapStateToProps = (state: any) => {
  return {
    organizationId: state.organization.id,
    organizationUsers: state.organizationUsers.data,
    theme: state.theme.isLightMode,
  };
};

export default connect(mapStateToProps)(HolidayConfigModel);
